import { decamelizeKeys } from "humps";
import cloneDeep from "lodash/cloneDeep";
import mapValues from "lodash/mapValues";
import omit from "lodash/omit";
import {
  FiltersV2Button,
  FiltersV2Panel,
  FiltersV2Preview,
  FiltersV2StoreContextProvider,
  useApiFilters,
  useFiltersV2MetaAutoUpdate,
  useOnFilterUpdate
} from "PFApp/components/filters_v2";
import SortBy from "PFApp/components/sort_by";
import { KEYWORD_FILTER_KEY } from "PFApp/search/tabs_constants.js";
import { Inline } from "PFComponents/containers";
import { isValueEmpty } from "PFCore/helpers/filters";
import { KnownFiltersV2 } from "PFTypes/filters_v2";
import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "./search_filters.module.scss";

const SearchFiltersContent = ({ pending, meta, applyRequestFilters, applyOrder, omitInSidePanel }) => {
  const { t } = useTranslation("search");
  useOnFilterUpdate(KnownFiltersV2.Availability, ({ value }) => {
    if (isValueEmpty(value)) {
      applyOrder({});
    }
  });

  useFiltersV2MetaAutoUpdate(meta);

  const { apiFilters } = useApiFilters({ sendDefaultsAsUndefined: false, sendEmptyAsUndefined: false });

  useEffect(() => {
    if (apiFilters) {
      applyRequestFilters(decamelizeKeys(apiFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFilters]);

  return (
    <Inline
      justifyContent="space-between"
      reverse
      className={css.filtersWrap}
      role="region"
      aria-label={t("filters")}
      qaId="SearchPage.filters"
    >
      <Inline center>
        {meta.order && (
          <SortBy
            sort={meta.order.value?.text}
            options={meta.order.options.map((option) => ({
              id: option.text,
              displayElement: option.text,
              item: option
            }))}
            onSortChange={(value) => {
              applyOrder(value);
            }}
          />
        )}
        <FiltersV2Button withLabel disabled={pending} />
      </Inline>
      <FiltersV2Preview omit={[KnownFiltersV2.TemplateKey, KEYWORD_FILTER_KEY]} />
      <FiltersV2Panel
        omit={{ main: omitInSidePanel }}
        savedFiltersProps={{ omit: { main: [KnownFiltersV2.TemplateKey] } }}
      />
    </Inline>
  );
};

const SearchFilters = ({ meta, omitCompletely = [], ...props }) => {
  const metaWithoutOmittedFilters = useMemo(() => {
    if (!meta?.filtersV2) {
      return null;
    }

    const metaDraft = cloneDeep(meta);

    metaDraft.filtersV2.mainFilters = omit(metaDraft.filtersV2.mainFilters, [
      KnownFiltersV2.Term, // on the search page, term filter is handled globally
      ...omitCompletely
    ]);

    // on search default filters should not be required, search has to be able to find any data entry
    metaDraft.filtersV2.mainFilters = mapValues(metaDraft.filtersV2.mainFilters, (filter) => ({
      ...filter,
      required: false
    }));

    return metaDraft;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, ...omitCompletely]); // since omitCompletely does not have to be memoized, we should check it's content

  if (!metaWithoutOmittedFilters) {
    return null;
  }

  return (
    <FiltersV2StoreContextProvider persistent={false}>
      <SearchFiltersContent {...props} meta={metaWithoutOmittedFilters} />
    </FiltersV2StoreContextProvider>
  );
};

SearchFilters.propTypes = {
  pending: PropTypes.bool,
  meta: PropTypes.shape({ filters: PropTypes.array, order: PropTypes.object }),
  requestFilters: PropTypes.object,
  applyRequestFilters: PropTypes.func,
  applyOrder: PropTypes.func,
  omitInSidePanel: PropTypes.arrayOf(PropTypes.string),
  omitCompletely: PropTypes.arrayOf(PropTypes.string)
};

export default SearchFilters;
